import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ConnectionService } from '../../service/connection.service';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';
import { PhoneInputComponent } from '../../utility/phone-input/phone-input.component';
import { SignaturePadComponent } from '../../utility/signature-pad/signature-pad.component';

const STEP_KEY = 'onboarding_step';

@Component({
  selector: 'app-onboarding',
  imports: [CommonModule, FormsModule, InfoTooltipComponent, PhoneInputComponent, SignaturePadComponent],
  templateUrl: './onboarding.component.html',
  styleUrl: './onboarding.component.css',
})
export class OnboardingComponent implements OnInit {
  step = 1;
  totalSteps = 3;
  /** Tutte le tappe del primo accesso: le ultime due sono Contratto (wizard) e Listino. */
  readonly journey = ['Chi sei', 'Studio', 'Firma', 'Contratto', 'Listino'];
  saving = false;
  emailError = false;

  // Step 1 — Categorie (multiselezione)
  selectedCategories: string[] = [];

  // Step 2 — Dati studio
  business_name = '';
  telephone = '';
  email = '';
  piva = '';
  address = '';

  // Step 3 — Firma
  @ViewChild(SignaturePadComponent) sigPad?: SignaturePadComponent;

  readonly categories = [
    { value: 'makeup', label: 'Makeup Artist' },
    { value: 'hair', label: 'Hair Stylist' },
    { value: 'photo', label: 'Fotografo/a' },
    { value: 'video', label: 'Videomaker' },
    { value: 'dj', label: 'DJ' },
    { value: 'band', label: 'Musica Live' },
    { value: 'florist', label: 'Fiorista' },
    { value: 'other', label: 'Altro' },
  ];

  constructor(
    private connection: ConnectionService,
    private router: Router,
  ) {}

  ngOnInit() {
    if (localStorage.getItem('onboarding_completed') === 'true') {
      this.router.navigate(['/home']);
      return;
    }
    // Dopo una ricarica si riparte dallo step dov'eri, con i dati già salvati
    const saved = +(localStorage.getItem(STEP_KEY) ?? 1);
    if (saved >= 1 && saved <= this.totalSteps) this.step = saved;
    this.connection.get(
      'auth/profile',
      (data: any) => this.prefill(data),
      () => {},
    );
  }

  private prefill(data: any) {
    const cats: string[] = data?.user?.professional_categories ?? [];
    if (!this.selectedCategories.length && cats.length) this.selectedCategories = [...cats];
    const c = data?.contract ?? {};
    this.business_name ||= c.business_name ?? '';
    this.telephone ||= c.telephone ?? '';
    this.email ||= c.email ?? '';
    this.piva ||= c.piva ?? '';
    this.address ||= c.address ?? '';
  }

  get progress(): number {
    return ((this.step - 1) / this.journey.length) * 100;
  }

  get canvasEmpty(): boolean {
    return this.sigPad?.isEmpty ?? true;
  }

  /** "Salta tutto" porta alla Home: niente contratto e listino in coda. */
  skipAll() {
    this.finish(false);
  }

  skipStep() {
    this.goToStep(this.step + 1);
  }

  next() {
    switch (this.step) {
      case 1: this.saveStep1(); break;
      case 2: this.saveStep2(); break;
      case 3: this.saveStep3(); break;
    }
  }

  back() {
    if (this.step > 1) this.goToStep(this.step - 1);
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  private goToStep(n: number) {
    if (n > this.totalSteps) { this.finish(); return; }
    this.step = n;
    localStorage.setItem(STEP_KEY, String(n));
  }

  clearSignature() {
    this.sigPad?.clear();
  }

  toggleCategory(value: string) {
    const idx = this.selectedCategories.indexOf(value);
    if (idx >= 0) this.selectedCategories.splice(idx, 1);
    else this.selectedCategories.push(value);
  }

  hasCategory(value: string): boolean {
    return this.selectedCategories.includes(value);
  }

  private saveStep1() {
    if (!this.selectedCategories.length) { this.goToStep(2); return; }
    this.saving = true;
    this.connection.put(
      'auth/update',
      { 'user.professional_categories': this.selectedCategories },
      () => { this.saving = false; this.goToStep(2); },
      () => { this.saving = false; this.goToStep(2); },
    );
  }

  private saveStep2() {
    this.emailError = false;
    if (this.email && !this.isValidEmail(this.email)) {
      this.emailError = true;
      return;
    }
    const hasData = this.business_name || this.telephone || this.email || this.piva || this.address;
    if (!hasData) { this.goToStep(3); return; }
    this.saving = true;
    this.connection.put(
      'auth/update',
      {
        'contract.business_name': this.business_name,
        'contract.telephone': this.telephone,
        'contract.email': this.email,
        'contract.piva': this.piva,
        'contract.address': this.address,
      },
      () => { this.saving = false; this.goToStep(3); },
      () => { this.saving = false; this.goToStep(3); },
    );
  }

  private saveStep3() {
    const blob = this.sigPad?.getBlob();
    if (!blob) { this.goToStep(4); return; }
    this.saving = true;
    const formData = new FormData();
    formData.append('signature', blob, 'signature.png');
    this.connection.post(
      'auth/upload-signature',
      formData,
      () => { this.saving = false; this.goToStep(4); },
      () => { this.saving = false; this.goToStep(4); },
    );
  }

  private finish(chain = true) {
    const done = () => {
      localStorage.removeItem(STEP_KEY);
      localStorage.setItem('onboarding_completed', 'true');
      if (!chain) {
        this.router.navigate(['/home']);
        return;
      }
      localStorage.setItem('contract_wizard_pending', 'true');
      localStorage.setItem('post_onboarding_chain', 'true');
      this.router.navigate(['/contratti']);
    };
    this.connection.put('auth/onboarding-complete', {}, done, done);
  }
}
